import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { isModelProblem, ModelStatus } from '../model/tenants/model-status.model';
import { isAdminRole } from '../model/tenants/user-profile.model';
import { AppService } from './app.service';
import { NotificationService } from './notification.service';
import { TenantClientService } from './tenant-client.service';
import { TranslateService } from './translate.service';

/**
 * Tells admins when the tenant's data model was refused or failed to load. Members and
 * viewers get 403 on the admin endpoints, so the status is only requested for admins.
 */
@Injectable({ providedIn: 'root' })
export class ModelStatusService {
  constructor(
    private appService: AppService,
    private tenantClient: TenantClientService,
    private notifications: NotificationService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  /** Checks the model status whenever an admin opens a tenant. Subscribe once, from the root component. */
  watch(): Observable<ModelStatus> {
    return combineLatest([this.appService.tenantSubject, this.appService.userProfileSubject]).pipe(
      filter(([tenant, profile]) => !!tenant && isAdminRole(profile?.role)),
      switchMap(([tenant]) =>
        this.tenantClient.getModelStatus(tenant!.tenantCode).pipe(
          // A failed status check must not break the app; the model page can be opened by hand.
          catchError(() => EMPTY),
          tap((status) => this.report(tenant!.tenantCode, status)),
        )
      ),
    );
  }

  private report(tenantCode: string, status: ModelStatus): void {
    if (!isModelProblem(status)) {
      return;
    }
    // Already on the model page: it shows the details itself.
    if (this.router.url.split('?')[0] === `/${tenantCode}/system/model`) {
      return;
    }
    const modelPage = ['/', tenantCode, 'system', 'model'];

    this.notifications.show({
      message: this.translate.get(status.state === 'FAILED' ? 'model.failed' : 'model.refused'),
      level: 'ERROR',
      link: { label: this.translate.get('model.view_details'), commands: modelPage },
    });
    if (status.state === 'FAILED') {
      this.router.navigate(modelPage);
    }
  }
}
